import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, CheckIcon, ChevronRightIcon, ChipIcon, CloudIcon, DownloadIcon, HeadphonesIcon, LockIcon, TrashIcon, SlidersIcon, PlusIcon, MinusIcon, GripVerticalIcon, HeartIcon } from '../components/icons';
import { AI_PROVIDERS, AiProviderId, AUDIO_LIBRARY, ModelState, SETTINGS_STUBS } from '../data/settings';
import { TINTS, RECOMMENDATIONS, FAVOURITES, FEATURES, SUPPORT } from '../data/mock';
import { useDashboardConfig, orderItems } from '../data/dashboardConfig';
import { useConditions, CONDITIONS } from '../data/conditions';
import { FullScreenModal } from '../components/FullScreenModal';
/* ---------- tiny local primitives ---------- */
function Toggle({
  on,
  onChange
}: {
  on: boolean;
  onChange: () => void;
}) {
  return <button onClick={onChange} className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-300 ${on ? 'bg-teal' : 'bg-linen-deep'}`} data-id="element-1017">
      <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-cream shadow-card transition-all duration-300 ${on ? 'left-[22px]' : 'left-0.5'}`} data-id="element-1018" />
    </button>;
}
function ProgressBar({
  duration = 2800,
  tint = 'bg-teal'
}: {
  duration?: number;
  tint?: string;
}) {
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFilled(true), 60);
    return () => clearTimeout(t);
  }, []);
  return <div className="h-1.5 w-full overflow-hidden rounded-full bg-linen-deep" data-id="element-1019">
      <div className={`h-full rounded-full ${tint}`} style={{
      width: filled ? '100%' : '4%',
      transition: `width ${duration}ms ease-out`
    }} data-id="element-1020" />
    </div>;
}
function Radio({
  active,
  tintSolid
}: {
  active: boolean;
  tintSolid: string;
}) {
  return <span className={`flex h-5.5 h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${active ? 'border-transparent ' + tintSolid : 'border-linen-deep bg-cream'}`} data-id="element-1021">
      {active && <CheckIcon size={13} strokeWidth={2.6} className="text-cream" data-id="element-1022" />}
    </span>;
}
/* ---------- Sub-views ---------- */
const MODEL_DOWNLOAD_MS = 3200;
const ALL_AUDIO_DOWNLOAD_MS = 4500;
function AiSettingsView({
  onBack
}: {
  onBack: () => void;
}) {
  const [providerId, setProviderId] = useState<AiProviderId>('local');
  const [activeModel, setActiveModel] = useState<Record<AiProviderId, string>>({
    local: 'gemma-3-1b',
    openrouter: 'llama-3-3-70b',
    sota: 'claude'
  });
  const [modelStates, setModelStates] = useState<Record<string, ModelState>>({
    'gemma-3-1b': 'downloaded',
    'gemma-3n-e2b': 'none',
    'gemma-3n-e4b': 'none'
  });
  const downloadModel = (modelId: string) => {
    setModelStates(s => ({
      ...s,
      [modelId]: 'downloading'
    }));
    setTimeout(() => setModelStates(s => ({
      ...s,
      [modelId]: 'downloaded'
    })), MODEL_DOWNLOAD_MS);
  };
  return <div className="h-full overflow-y-auto pb-48" data-id="element-1023">
      <header className="px-5 pt-4" data-id="element-1024">
        <div className="flex items-center gap-3" data-id="element-1025">
          <button onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-full bg-cream text-ink shadow-card transition-transform active:scale-90" data-id="element-1026">
            <ArrowLeftIcon size={18} data-id="element-1027" />
          </button>
          <div data-id="element-1028">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-teal" data-id="element-1029">
              Settings
            </p>
            <h1 className="font-display text-[24px] font-semibold leading-tight text-ink" data-id="element-1030">
              AI assistant
            </h1>
          </div>
        </div>
      </header>
      <section className="mt-6 px-5" data-id="element-1031">
        <p className="text-[12.5px] leading-snug text-ink-soft" data-id="element-1032">
          Choose where Sunny's understanding runs. Search and navigation always
          work — this only changes how questions are interpreted.
        </p>
        <div className="mt-3.5 flex flex-col gap-2.5" data-id="element-1033">
          {AI_PROVIDERS.map(provider => {
          const tint = TINTS[provider.tint];
          const selected = providerId === provider.id;
          return <div key={provider.id} className={`overflow-hidden rounded-card border-2 transition-all duration-300 ${selected ? `${tint.border.replace('border-', 'border-')} ${tint.soft}` : 'border-linen-deep bg-cream'}`} style={selected ? {
            borderColor: 'transparent'
          } : undefined} data-id="element-1034">
                <button onClick={() => setProviderId(provider.id)} className="flex w-full items-center gap-3 p-4 text-left" data-id="element-1035">
                  <Radio active={selected} tintSolid={tint.solid} data-id="element-1036" />
                  <div className="min-w-0 flex-1" data-id="element-1037">
                    <div className="flex items-center gap-2" data-id="element-1038">
                      <h3 className="font-display text-[16px] font-semibold text-ink" data-id="element-1039">
                        {provider.title}
                      </h3>
                      {provider.badge && <span className={`flex items-center gap-1 rounded-full ${tint.solid} px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.08em] text-cream`} data-id="element-1040">
                          <LockIcon size={9} strokeWidth={2.4} data-id="element-1041" />
                          {provider.badge}
                        </span>}
                    </div>
                    <p className="mt-0.5 text-[12.5px] font-medium text-ink-soft" data-id="element-1042">
                      {provider.subtitle}
                    </p>
                  </div>
                </button>
                {selected && <div className="dhp-fade px-4 pb-4" data-id="element-1043">
                    <div className="flex flex-col gap-2" data-id="element-1044">
                      {provider.models.map(model => {
                  const state: ModelState = model.size ? modelStates[model.id] : 'downloaded';
                  const isActive = activeModel[provider.id] === model.id;
                  const selectable = state === 'downloaded';
                  return <div key={model.id} className="rounded-2xl bg-cream p-3.5 shadow-card" data-id="element-1045">
                            <div className="flex items-center gap-3" data-id="element-1046">
                              <button onClick={() => selectable && setActiveModel(a => ({
                        ...a,
                        [provider.id]: model.id
                      }))} className={`flex min-w-0 flex-1 items-center gap-3 text-left ${selectable ? '' : 'opacity-60'}`} data-id="element-1047">
                                <Radio active={isActive && selectable} tintSolid={tint.solid} data-id="element-1048" />
                                <span className="min-w-0 flex-1" data-id="element-1049">
                                  <span className="flex items-baseline gap-2" data-id="element-1050">
                                    <span className="font-display text-[14.5px] font-semibold text-ink" data-id="element-1051">
                                      {model.name}
                                    </span>
                                    {model.size && <span className="text-[11px] font-bold text-ink-faint" data-id="element-1052">
                                        {model.size}
                                      </span>}
                                  </span>
                                  <span className="block text-[11.5px] font-medium leading-snug text-ink-soft" data-id="element-1053">
                                    {model.desc}
                                  </span>
                                  <span className="block text-[10.5px] font-semibold text-ink-faint" data-id="element-1054">
                                    {model.by}
                                  </span>
                                </span>
                              </button>
                              {model.size && state === 'none' && <button onClick={() => downloadModel(model.id)} className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${tint.solid} text-cream shadow-card transition-transform hover:scale-105 active:scale-95`} data-id="element-1055">
                                  <DownloadIcon size={16} strokeWidth={2} data-id="element-1056" />
                                </button>}
                              {model.size && state === 'downloaded' && <span className={`flex items-center gap-1 text-[11px] font-bold ${tint.text}`} data-id="element-1057">
                                  <CheckIcon size={12} strokeWidth={2.6} data-id="element-1058" />
                                  On device
                                </span>}
                            </div>
                            {model.size && state === 'downloading' && <div className="mt-3" data-id="element-1059">
                                <ProgressBar duration={MODEL_DOWNLOAD_MS} tint={tint.solid} data-id="element-1060" />
                                <p className="mt-1.5 text-[10.5px] font-semibold text-ink-faint" data-id="element-1061">
                                  Downloading {model.size}… you can keep using
                                  the app
                                </p>
                              </div>}
                          </div>;
                })}
                    </div>
                    <div className="mt-3 flex items-start gap-2" data-id="element-1062">
                      <LockIcon size={13} className={`mt-0.5 shrink-0 ${tint.text}`} data-id="element-1063" />
                      <p className="text-[11.5px] leading-snug text-ink-soft" data-id="element-1064">
                        {provider.privacy}
                      </p>
                    </div>
                  </div>}
              </div>;
        })}
        </div>
      </section>
    </div>;
}
function AudioSettingsView({
  onBack
}: {
  onBack: () => void;
}) {
  const [onDemand, setOnDemand] = useState(true);
  const [allAudio, setAllAudio] = useState<'idle' | 'downloading' | 'done'>('idle');
  const downloadAllAudio = () => {
    setAllAudio('downloading');
    setTimeout(() => setAllAudio('done'), ALL_AUDIO_DOWNLOAD_MS);
  };
  return <div className="h-full overflow-y-auto pb-48" data-id="element-1065">
      <header className="px-5 pt-4" data-id="element-1066">
        <div className="flex items-center gap-3" data-id="element-1067">
          <button onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-full bg-cream text-ink shadow-card transition-transform active:scale-90" data-id="element-1068">
            <ArrowLeftIcon size={18} data-id="element-1069" />
          </button>
          <div data-id="element-1070">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-teal" data-id="element-1071">
              Settings
            </p>
            <h1 className="font-display text-[24px] font-semibold leading-tight text-ink" data-id="element-1072">
              Narration audio
            </h1>
          </div>
        </div>
      </header>
      <section className="mt-6 px-5" data-id="element-1073">
        <p className="text-[12.5px] leading-snug text-ink-soft" data-id="element-1074">
          Sunny's spoken articles and guidance. Created by your care team and
          synced from the cloud.
        </p>
        <div className="mt-3.5 rounded-card bg-cream p-4 shadow-card" data-id="element-1075">
          <div className="flex items-center justify-between gap-3" data-id="element-1076">
            <div data-id="element-1077">
              <h3 className="font-display text-[15px] font-semibold text-ink" data-id="element-1078">
                Download on demand
              </h3>
              <p className="mt-0.5 text-[12px] leading-snug text-ink-soft" data-id="element-1079">
                Tracks download the first time you play them, then stay on your
                phone.
              </p>
            </div>
            <Toggle on={onDemand} onChange={() => setOnDemand(o => !o)} data-id="element-1080" />
          </div>
        </div>
        <div className="mt-2.5 rounded-card bg-cream p-4 shadow-card" data-id="element-1081">
          <div className="flex items-center justify-between gap-3" data-id="element-1082">
            <div className="min-w-0 flex-1" data-id="element-1083">
              <h3 className="font-display text-[15px] font-semibold text-ink" data-id="element-1084">
                Download everything
              </h3>
              <p className="mt-0.5 text-[12px] leading-snug text-ink-soft" data-id="element-1085">
                {AUDIO_LIBRARY.totalTracks} tracks · {AUDIO_LIBRARY.totalSize} —
                listen anywhere, even offline.
              </p>
            </div>
            {allAudio === 'idle' && <button onClick={downloadAllAudio} className="flex shrink-0 items-center gap-1.5 rounded-full bg-teal px-4 py-2.5 text-[12.5px] font-bold text-cream shadow-card transition-transform hover:scale-105 active:scale-95" data-id="element-1086">
                <DownloadIcon size={14} strokeWidth={2.2} data-id="element-1087" />
                Download all
              </button>}
            {allAudio === 'done' && <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-sage-soft px-3.5 py-2 text-[12px] font-bold text-sage-deep" data-id="element-1088">
                <CheckIcon size={13} strokeWidth={2.6} data-id="element-1089" />
                All on device
              </span>}
          </div>
          {allAudio === 'downloading' && <div className="mt-3.5" data-id="element-1090">
              <ProgressBar duration={ALL_AUDIO_DOWNLOAD_MS} tint="bg-teal" data-id="element-1091" />
              <p className="mt-1.5 text-[10.5px] font-semibold text-ink-faint" data-id="element-1092">
                Downloading {AUDIO_LIBRARY.totalSize} — this happens in the
                background
              </p>
            </div>}
        </div>
        <div className="mt-2.5 overflow-hidden rounded-card bg-cream shadow-card" data-id="element-1093">
          {AUDIO_LIBRARY.categories.map((cat, i) => {
          const allDone = allAudio === 'done';
          const downloaded = allDone ? cat.tracks : cat.downloaded;
          const complete = downloaded === cat.tracks;
          return <div key={cat.id} className={`flex items-center justify-between gap-3 p-4 ${i > 0 ? 'border-t border-linen' : ''}`} data-id="element-1094">
                <div className="min-w-0 flex-1" data-id="element-1095">
                  <h4 className="text-[13.5px] font-bold text-ink" data-id="element-1096">
                    {cat.name}
                  </h4>
                  <p className="mt-0.5 text-[11.5px] font-medium text-ink-faint" data-id="element-1097">
                    {downloaded} of {cat.tracks} tracks on device · {cat.size}
                  </p>
                </div>
                {complete ? <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sage-soft text-sage-deep" data-id="element-1098">
                    <CheckIcon size={14} strokeWidth={2.4} data-id="element-1099" />
                  </span> : <button className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-soft text-teal-deep transition-transform hover:scale-105 active:scale-95" data-id="element-1100">
                    <DownloadIcon size={14} strokeWidth={2.2} data-id="element-1101" />
                  </button>}
              </div>;
        })}
        </div>
        <div className="mt-2.5 flex items-center justify-between rounded-card bg-linen-deep p-4" data-id="element-1102">
          <div data-id="element-1103">
            <p className="text-[12.5px] font-bold text-ink" data-id="element-1104">
              {allAudio === 'done' ? AUDIO_LIBRARY.totalSize : AUDIO_LIBRARY.onDeviceSize}{' '}
              on this phone
            </p>
            <p className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-ink-soft" data-id="element-1105">
              <CloudIcon size={12} data-id="element-1106" />
              {AUDIO_LIBRARY.lastSync}
            </p>
          </div>
          <button className="flex items-center gap-1.5 rounded-full bg-cream px-3.5 py-2 text-[12px] font-bold text-coral-deep shadow-card transition-transform hover:scale-105 active:scale-95" data-id="element-1107">
            <TrashIcon size={13} strokeWidth={2} data-id="element-1108" />
            Free up space
          </button>
        </div>
      </section>
    </div>;
}
function DashboardSettingsView({
  onBack
}: {
  onBack: () => void;
}) {
  const {
    getOrder,
    getLimit,
    setOrder,
    setLimit
  } = useDashboardConfig();
  const sections = [{
    key: 'heads-up' as const,
    label: 'HEADS UP',
    source: RECOMMENDATIONS
  }, {
    key: 'favourites' as const,
    label: 'FAVOURITES',
    source: FAVOURITES
  }, {
    key: 'spaces' as const,
    label: 'SPACES',
    source: FEATURES
  }, {
    key: 'support' as const,
    label: 'SUPPORT',
    source: SUPPORT
  }];
  const [drag, setDrag] = useState<{
    key: 'heads-up' | 'favourites' | 'spaces' | 'support';
    index: number;
  } | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const dropReorder = (key: 'heads-up' | 'favourites' | 'spaces' | 'support', targetIndex: number) => {
    if (!drag || drag.key !== key || drag.index === targetIndex) {
      setDrag(null);
      setOverIndex(null);
      return;
    }
    const currentOrder = [...getOrder(key)];
    const [moved] = currentOrder.splice(drag.index, 1);
    currentOrder.splice(targetIndex, 0, moved);
    setOrder(key, currentOrder);
    setDrag(null);
    setOverIndex(null);
  };
  return <div className="h-full overflow-y-auto pb-48" data-id="element-1109">
      <header className="px-5 pt-4" data-id="element-1110">
        <div className="flex items-center gap-3" data-id="element-1111">
          <button onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-full bg-cream text-ink shadow-card transition-transform active:scale-90" data-id="element-1112">
            <ArrowLeftIcon size={18} data-id="element-1113" />
          </button>
          <div data-id="element-1114">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-teal" data-id="element-1115">
              Settings
            </p>
            <h1 className="font-display text-[24px] font-semibold leading-tight text-ink" data-id="element-1116">
              Dashboard
            </h1>
          </div>
        </div>
      </header>
      <section className="mt-6 px-5 space-y-8" data-id="element-1117">
        {sections.map(({
        key,
        label,
        source
      }) => {
        const limit = getLimit(key);
        const orderedItems = orderItems(source, getOrder(key));
        return <div key={key} data-id="element-1118">
              <div className="flex items-center justify-between mb-3" data-id="element-1119">
                <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-ink-faint" data-id="element-1120">
                  {label}
                </p>
                <div className="flex items-center gap-3" data-id="element-1121">
                  <span className="text-[11.5px] font-semibold text-ink-soft" data-id="element-1122">
                    Show on home:
                  </span>
                  <div className="flex items-center gap-2 rounded-full bg-cream px-2 py-1 shadow-card border border-line" data-id="element-1123">
                    <button onClick={() => setLimit(key, Math.max(0, limit - 1))} className="flex h-6 w-6 items-center justify-center rounded-full text-ink-soft hover:bg-linen transition-colors" data-id="element-1124">
                      <MinusIcon size={14} data-id="element-1125" />
                    </button>
                    <span className="w-4 text-center text-[13px] font-bold text-ink" data-id="element-1126">
                      {limit}
                    </span>
                    <button onClick={() => setLimit(key, Math.min(source.length, limit + 1))} className="flex h-6 w-6 items-center justify-center rounded-full text-ink-soft hover:bg-linen transition-colors" data-id="element-1127">
                      <PlusIcon size={14} data-id="element-1128" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-2.5" data-id="element-1129">
                {orderedItems.map((item, index) => {
              const tint = TINTS[item.tint];
              const Icon = item.icon;
              const isHidden = index >= limit;
              const isDragging = drag?.key === key && drag.index === index;
              const isDropTarget = drag?.key === key && overIndex === index && !isDragging;
              return <div key={item.id} draggable onDragStart={() => {
                setDrag({
                  key,
                  index
                });
                setOverIndex(index);
              }} onDragOver={e => {
                e.preventDefault();
                if (drag?.key === key) setOverIndex(index);
              }} onDrop={e => {
                e.preventDefault();
                dropReorder(key, index);
              }} onDragEnd={() => {
                setDrag(null);
                setOverIndex(null);
              }} className={`flex w-full items-center gap-3.5 rounded-card border bg-cream px-4 py-3.5 text-left shadow-card transition-all ${isDragging ? 'border-teal opacity-40' : isDropTarget ? 'border-teal ring-2 ring-teal/30' : 'border-line'} ${isHidden ? 'opacity-60' : ''}`} data-id="element-1130">
                      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tint.soft} ${tint.text}`} data-id="element-1131">
                        <Icon size={22} data-id="element-1132" />
                      </span>
                      <div className="min-w-0 flex-1" data-id="element-1133">
                        <div className="flex items-center gap-2" data-id="element-1134">
                          <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-1135">
                            {item.title}
                          </h3>
                          {isHidden && <span className="rounded-full bg-linen-deep px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-ink-soft" data-id="element-1136">
                              Hidden
                            </span>}
                        </div>
                        {('subtitle' in item || 'hint' in item || 'body' in item) && <p className="mt-0.5 truncate text-[13px] leading-snug text-ink-soft" data-id="element-1137">
                            {('subtitle' in item ? item.subtitle : 'hint' in item ? item.hint : 'body' in item ? item.body : '') as string}
                          </p>}
                      </div>
                      <span className="flex h-9 w-9 shrink-0 cursor-grab items-center justify-center rounded-lg text-ink-faint transition-colors hover:bg-linen active:cursor-grabbing" aria-label="Drag to reorder" data-id="element-1138">
                        <GripVerticalIcon size={18} data-id="element-1139" />
                      </span>
                    </div>;
            })}
              </div>
            </div>;
      })}
      </section>
    </div>;
}
function ConditionsSettingsView({
  onBack
}: {
  onBack: () => void;
}) {
  const {
    isSelected,
    toggle,
    selected
  } = useConditions();
  return <div className="h-full overflow-y-auto pb-48" data-id="element-1140">
      <header className="px-5 pt-4" data-id="element-1141">
        <div className="flex items-center gap-3" data-id="element-1142">
          <button onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-full bg-cream text-ink shadow-card transition-transform active:scale-90" data-id="element-1143">
            <ArrowLeftIcon size={18} data-id="element-1144" />
          </button>
          <div data-id="element-1145">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-teal" data-id="element-1146">
              Settings
            </p>
            <h1 className="font-display text-[24px] font-semibold leading-tight text-ink" data-id="element-1147">
              Conditions
            </h1>
          </div>
        </div>
      </header>
      <section className="mt-6 px-5" data-id="element-1148">
        <p className="text-[12.5px] leading-snug text-ink-soft" data-id="element-1149">
          Tell Sunny what you're managing. Each condition you select unlocks its
          own Journey and tailors what you see.
        </p>
        <div className="mt-4 space-y-2.5" data-id="element-1150">
          {CONDITIONS.map(condition => {
          const tint = TINTS[condition.tint];
          const Icon = condition.icon;
          const on = isSelected(condition.id);
          return <button key={condition.id} onClick={() => toggle(condition.id)} className={`flex w-full items-center gap-3.5 rounded-card border bg-cream px-4 py-3.5 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98] ${on ? 'border-teal' : 'border-line'}`} data-id="element-1151">
                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tint.soft} ${tint.text}`} data-id="element-1152">
                  <Icon size={22} data-id="element-1153" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-1154">
                  <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-1155">
                    {condition.label}
                  </h3>
                  <p className="mt-0.5 truncate text-[13px] leading-snug text-ink-soft" data-id="element-1156">
                    {condition.programme}
                  </p>
                </div>
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${on ? 'border-transparent bg-teal text-cream' : 'border-linen-deep bg-cream'}`} data-id="element-1157">
                  {on && <CheckIcon size={14} strokeWidth={2.8} data-id="element-1158" />}
                </span>
              </button>;
        })}
        </div>
        {selected.length === 0 && <p className="mt-4 text-center text-[12.5px] font-medium text-coral-deep" data-id="element-1159">
            Select at least one condition to personalise your Journey.
          </p>}
      </section>
    </div>;
}
/* ---------- the screen ---------- */
type ViewState = 'hub' | 'dashboard' | 'ai' | 'audio' | 'conditions';
export function SettingsScreen({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [view, setView] = useState<ViewState>('hub');
  // re-seed to the hub each time settings opens
  useEffect(() => {
    if (open) setView('hub');
  }, [open]);
  const back = () => setView('hub');
  let content: React.ReactNode;
  if (view === 'dashboard') content = <DashboardSettingsView onBack={back} data-id="element-1160" />;else if (view === 'conditions') content = <ConditionsSettingsView onBack={back} data-id="element-1161" />;else if (view === 'ai') content = <AiSettingsView onBack={back} data-id="element-1162" />;else if (view === 'audio') content = <AudioSettingsView onBack={back} data-id="element-1163" />;else content = <div className="h-full overflow-y-auto pb-48" data-id="element-1164">
        <header className="px-5 pt-4" data-id="element-1165">
          <div className="flex items-center gap-3" data-id="element-1166">
            <div data-id="element-1167">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-teal" data-id="element-1168">
                Make it yours
              </p>
              <h1 className="font-display text-[24px] font-semibold leading-tight text-ink" data-id="element-1169">
                Settings
              </h1>
            </div>
          </div>
        </header>

        <section className="mt-8 px-5 space-y-2.5" data-id="element-1170">
          <button onClick={() => setView('dashboard')} className="flex w-full items-center gap-3.5 rounded-card border border-line bg-cream px-4 py-3.5 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]" data-id="element-1171">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-soft text-sky-deep" data-id="element-1172">
              <SlidersIcon size={22} data-id="element-1173" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-1174">
              <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-1175">
                Dashboard
              </h3>
              <p className="mt-0.5 truncate text-[13px] leading-snug text-ink-soft" data-id="element-1176">
                Reorder sections & set how many show
              </p>
            </div>
            <ChevronRightIcon size={18} strokeWidth={2.4} className="shrink-0 text-ink-faint" data-id="element-1177" />
          </button>

          <button onClick={() => setView('conditions')} className="flex w-full items-center gap-3.5 rounded-card border border-line bg-cream px-4 py-3.5 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]" data-id="element-1178">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-coral-soft text-coral-deep" data-id="element-1179">
              <HeartIcon size={22} data-id="element-1180" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-1181">
              <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-1182">
                Conditions
              </h3>
              <p className="mt-0.5 truncate text-[13px] leading-snug text-ink-soft" data-id="element-1183">
                What you're managing & your journeys
              </p>
            </div>
            <ChevronRightIcon size={18} strokeWidth={2.4} className="shrink-0 text-ink-faint" data-id="element-1184" />
          </button>

          <button onClick={() => setView('ai')} className="flex w-full items-center gap-3.5 rounded-card border border-line bg-cream px-4 py-3.5 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]" data-id="element-1185">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sage-soft text-sage-deep" data-id="element-1186">
              <ChipIcon size={22} data-id="element-1187" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-1188">
              <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-1189">
                AI assistant
              </h3>
              <p className="mt-0.5 truncate text-[13px] leading-snug text-ink-soft" data-id="element-1190">
                Where Sunny's understanding runs
              </p>
            </div>
            <ChevronRightIcon size={18} strokeWidth={2.4} className="shrink-0 text-ink-faint" data-id="element-1191" />
          </button>

          <button onClick={() => setView('audio')} className="flex w-full items-center gap-3.5 rounded-card border border-line bg-cream px-4 py-3.5 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]" data-id="element-1192">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-lilac-soft text-lilac-deep" data-id="element-1193">
              <HeadphonesIcon size={22} data-id="element-1194" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-1195">
              <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-1196">
                Narration audio
              </h3>
              <p className="mt-0.5 truncate text-[13px] leading-snug text-ink-soft" data-id="element-1197">
                Spoken articles & downloads
              </p>
            </div>
            <ChevronRightIcon size={18} strokeWidth={2.4} className="shrink-0 text-ink-faint" data-id="element-1198" />
          </button>
        </section>

        <section className="mt-8 px-5" data-id="element-1199">
          <h2 className="font-display text-[17px] font-semibold text-ink" data-id="element-1200">
            More
          </h2>
          <div className="mt-3 overflow-hidden rounded-card bg-cream shadow-card" data-id="element-1201">
            {SETTINGS_STUBS.map((stub, i) => <button key={stub.id} className={`flex w-full items-center justify-between gap-3 p-4 text-left transition-colors hover:bg-linen ${i > 0 ? 'border-t border-linen' : ''}`} data-id="element-1202">
                <span className="text-[14px] font-bold text-ink" data-id="element-1203">
                  {stub.title}
                </span>
                <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-faint" data-id="element-1204">
                  {stub.value}
                  <ChevronRightIcon size={14} data-id="element-1205" />
                </span>
              </button>)}
          </div>
        </section>
      </div>;
  return <FullScreenModal open={open} onClose={onClose} direction="up" padded={false} scroll={false} resetOnOpen={false} data-id="element-1206">
      {content}
    </FullScreenModal>;
}